import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {FormattedRelativeTime, injectIntl} from 'react-intl';
import Moment from 'moment';
import RichTextField from '../../../../../../components/new-ui/rich_text_field';
import Person from '../../../person/person';
import {BUTTON_COLOR, BUTTON_STYLE} from '../../../../../../constants';
import ActionsMenu from '../../../action-menu/actions_menu';
import UpdateCommentMutation from '../../../../../../mutations/update_comment_mutation';
import DeleteCommentMutation from '../../../../../../mutations/delete_comment_mutation';
import Util from '../../../../util/util';
import Button from '../../../buttons/button/button';
import {convertToRaw} from 'draft-js';
import {MODAL_TYPE, showModal} from '../../../modals/generic_modal_conductor';
import Warning from '../../../../../../components/warning';
import {getTimeDifferenceAndUnit} from '../../../../util/TimeDifferenceUtil';
import {hasPermission} from '../../../../util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../../../../Permissions';
import {profilePicSrc} from '../../../../../../directApi';
import {hasFeatureFlag} from '../../../../util/FeatureUtil';
import {RichTextEditorDraftJsWrapper} from '../../../rich-text-editor-draftjs-wrapper/RichTextEditorDraftJsWrapper';
import {convertEditedHtmlOrNotify} from '../../../rich-text-editor-draftjs-wrapper/convertEditedHtml';
import {isEditorEmpty} from '../../../rich-text-editor-draftjs-wrapper/isEditorEmpty';
import {
	EDITOR_SURFACE,
	trackEditorSaveFailed,
	trackEditorSaveSucceeded,
} from '../../../../../../tracking/amplitude/editorEvents';
import {TASK_MODAL_PLUGIN_TOGGLES} from '../../taskModalPluginToggles';
import uuid from 'uuid';
import {CommentReactions} from './CommentReactions';

class Comment extends Component {
	constructor(props) {
		super(props);

		this.state = {
			isInEditMode: false,
			commentPlateJsInputId: uuid.v4(),
		};
	}

	startEditingComment() {
		this.setState({isInEditMode: true});
	}

	stopEditingComment() {
		this.setState({isInEditMode: false});
		if (hasFeatureFlag('plate_js_task_modal_comments')) {
			this.setState({commentPlateJsInputId: uuid.v4()});
		} else {
			this.richTextField.getDecoratedComponentInstance().setText(this.props.comment.node.comment || '');
		}
	}

	handleSubmitShortcut() {
		// ONE editor instance per comment with readOnly toggled, so a mounted editor is not
		// necessarily an editable one. Without this, Enter on a comment the user is merely
		// reading would commit an update mutation.
		//
		// Both guards return false rather than nothing, which hands the key back to the
		// editor so Enter still inserts a paragraph instead of vanishing.
		// Defence in depth, and not reachable through the DOM: readOnly={!isInEditMode}
		// reaches PlateContent, and slate-react will not call onKeyDown while read-only. It
		// stays because the handler is also invoked directly by tests and could be wired to
		// another surface later. Do not remove it as dead code.
		if (!this.state.isInEditMode) return false;

		// Read ONCE. getHtmlValue runs removeInlinePopups, which removeNodes()s mention-input
		// nodes before serialising — so calling it as a predicate and again for the payload
		// both serialises twice and mutates the document on the decline path.
		const html = this.plateRichTextField?.getHtmlValue();
		if (isEditorEmpty(html)) return false;
		this.saveComment(html);
	}

	/** `html` is supplied by the Enter path, which has already serialised. The save control omits it. */
	saveComment(html) {
		let commentText;
		if (hasFeatureFlag('plate_js_task_modal_comments')) {
			// Converted before edit mode is left, not after. Leaving edit mode first takes
			// the editor — and with it the only copy of the user's text — off the screen,
			// so a conversion failure there loses the edit with nothing to copy back.
			commentText = convertEditedHtmlOrNotify(
				html ?? this.plateRichTextField.getHtmlValue(),
				this.props.intl,
				EDITOR_SURFACE.commentEdit
			);
			if (commentText === null) return;
		} else {
			commentText = JSON.stringify(
				convertToRaw(this.richTextField.getDecoratedComponentInstance().state.editorState.getCurrentContent())
			);
		}
		// Tell the modal which inline images this comment just lost, and what it now
		// holds. Deletion is deferred to modal close, so an image removed here and
		// restored elsewhere in the same session costs the user nothing.
		this.setState({isInEditMode: false});

		this.props.onInlineImagesChanged?.(this.props.comment.node.id, this.props.comment.node.comment, commentText);

		Util.CommitMutation(
			UpdateCommentMutation,
			{
				id: this.props.comment.node.id,
				comment: commentText,
			},
			() => trackEditorSaveSucceeded(EDITOR_SURFACE.commentEdit),
			true,
			null,
			// The fallback runs either way, so the correlation-ID error modal and the retry
			// are untouched. A scheduled retry is not a failure yet.
			(transaction, onFailure) => {
				if (!onFailure(transaction)) {
					trackEditorSaveFailed(EDITOR_SURFACE.commentEdit, 'mutation');
				}
			}
		);
	}

	deleteComment() {
		const {formatMessage} = this.props.intl;
		showModal({
			type: MODAL_TYPE.GENERIC,
			content: (
				<div className="default-warning-modal">
					<Warning messageId="common.delete-confirmation-title" />
					<div className="warning-part-2">{formatMessage({id: 'common.warning.this_action_can_not_be_undone'})}</div>
				</div>
			),
			buttons: [
				{
					text: formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: formatMessage({id: 'common.delete'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.RED,
					callback: () => {
						// Deleting the comment takes its inline images with it. They join the
						// same pending set as an ordinary removal, so a file still referenced
						// by the description or another comment survives.
						this.props.onInlineImagesChanged?.(this.props.comment.node.id, this.props.comment.node.comment, '');

						Util.CommitMutation(DeleteCommentMutation, {
							id: this.props.comment.node.id,
							taskId: this.props.taskId,
						});
					},
				},
			],
		});
	}

	getActionMenuOptions() {
		const {formatMessage} = this.props.intl;

		const actionMenuOptions = [];

		if (!this.state.isInEditMode) {
			actionMenuOptions.push({
				text: formatMessage({id: 'common.edit'}),
				onClick: this.startEditingComment.bind(this),
			});
		}

		actionMenuOptions.push({
			text: formatMessage({id: 'common.delete'}),
			onClick: this.deleteComment.bind(this),
		});

		return actionMenuOptions;
	}

	render() {
		const {isViewerComment, isTaskReadOnly, project, viewer} = this.props;
		const {formatMessage} = this.props.intl;
		const {isInEditMode} = this.state;
		const comment = this.props.comment.node;
		const person = comment.person;
		// FOR-908: a Client Portal comment is stored under the service person; the contact who
		// wrote it rides along as authorClientContact. A guest viewer gets the contact with null
		// names (guest-gated to `id`), so an empty name falls back to the person.
		const contact = comment.authorClientContact;
		const contactName = contact ? [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim() : '';
		const authorName = contactName || person.fullName || `${person.firstName} ${person.lastName}`;

		const commentCreationDate = Moment.utc({
			y: comment.year,
			M: comment.month - 1,
			d: comment.day,
			h: comment.hours,
			m: comment.minutes,
			s: comment.seconds,
		});

		const actionMenuOptions = this.getActionMenuOptions();

		const mentions =
			project.projectPersons &&
			project.projectPersons.edges
				.filter(projectPerson => {
					return projectPerson.node.person.active;
				})
				.map(projectPerson => {
					return {
						personId: projectPerson.node.person.id,
						name: projectPerson.node.person.firstName + ' ' + projectPerson.node.person.lastName,
						avatar: profilePicSrc(projectPerson.node.person.profilePictureId),
					};
				});

		const plateJsMentionsArray =
			project.projectPersons &&
			project.projectPersons.edges
				.filter(projectPerson => {
					return projectPerson.node.person.active;
				})
				.map(projectPerson => {
					return {
						id: projectPerson.node.person.id,
						text: projectPerson.node.person.firstName + ' ' + projectPerson.node.person.lastName,
					};
				});

		const mentionsLabels = viewer.company.labels.edges.map(label => {
			return {id: label.node.id, name: label.node.name};
		});

		const {value, unit} = getTimeDifferenceAndUnit(commentCreationDate);

		return (
			<div className="comment">
				<div className="profile-picture">
					<Person
						name={authorName}
						showName={false}
						showRole={false}
						imageSize="medium"
						// The whole hexagon, points and all - see Person's `unclipped`.
						unclipped
						// A contact has no profile picture: null renders their initials.
						imageSrc={contactName ? null : profilePicSrc(person.profilePictureId)}
					/>
				</div>
				<div className="comment-section">
					<div className="comment-header">
						<div className="person-name">{contactName || person.fullName}</div>
						<div className="time-ago" title={commentCreationDate.format('LLL')}>
							{unit === 'month' || unit === 'year' ? (
								commentCreationDate.format('LL')
							) : (
								<FormattedRelativeTime
									numeric="auto"
									value={unit === 'second' && value > -10 ? 0 : value}
									unit={unit}
								/>
							)}
						</div>
					</div>
					{hasFeatureFlag('plate_js_task_modal_comments') ? (
						<RichTextEditorDraftJsWrapper
							id={this.state.commentPlateJsInputId}
							peopleMentions={plateJsMentionsArray}
							editorRef={e => (this.plateRichTextField = e)}
							initialDraftJsContent={comment.comment}
							// Only in edit mode. This is one editor instance with `readOnly` toggled
							// (RichTextEditor.js keeps the instance and flips the prop), and the editor
							// events are raised from a mount effect keyed on `surface` — so an
							// unconditional surface counted one editor open per comment on the screen
							// and none when a user actually started editing. Same reasoning as the
							// drift budget: a task modal mounts one editor per comment.
							surface={isInEditMode ? EDITOR_SURFACE.commentEdit : undefined}
							// PIKA-538 — THESE TWO PROPS ARE THE READ-ONLY STYLING. Deliberate, not
							// incidental: `readOnly` alone changes no paint, so a displayed comment
							// would otherwise render as an input. Do not "tidy" either of them away.
							//
							// `noBorder` drops the box, the 80px floor and the px-4 that indented the
							// body 16px past the author's name. Not passing maxHeight lets a long
							// comment flow instead of scrolling inside 190px. Both revert on edit.
							//
							// Why per-surface and not in the design system: read-only is deliberately
							// NOT uniform here. The legacy editor keeps the task DESCRIPTION bordered
							// (description_section.scss) while leaving comments unboxed, and matching
							// legacy is this ticket's objective. A shared "read-only = no chrome" rule
							// would fix comments and break the description. Revisit only if the product
							// asks for consistent read-only styling across surfaces.
							//
							// `variant` reaches the design system through the wrapper's {...rest}.
							variant={isInEditMode ? undefined : 'noBorder'}
							maxHeight={isInEditMode ? '190px' : undefined}
							placeholder={formatMessage({id: 'cards.comment'})}
							pluginToggles={TASK_MODAL_PLUGIN_TOGGLES}
							readOnly={!isInEditMode}
							imageAdapter={this.props.imageAdapter}
							onSubmitShortcut={this.handleSubmitShortcut.bind(this)}
						/>
					) : (
						<RichTextField
							mentions={mentions}
							mentionsLabels={mentionsLabels}
							ref={e => (this.richTextField = e)}
							text={comment.comment}
							alwaysShowControls={false}
							readOnly={!isInEditMode}
						/>
					)}
					{isInEditMode ? (
						<div className="edit-mode-buttons">
							<Button
								text={formatMessage({id: 'common.cancel'})}
								colorTheme={BUTTON_COLOR.WHITE}
								onClick={this.stopEditingComment.bind(this)}
								buttonStyle={BUTTON_STYLE.FILLED}
							/>
							<Button
								text={formatMessage({id: 'common.save'})}
								colorTheme={BUTTON_COLOR.GREEN}
								// Arrow rather than bind: saveComment now takes an optional html argument, and a
								// bound click handler would hand it the SyntheticEvent instead.
								onClick={() => this.saveComment()}
								buttonStyle={BUTTON_STYLE.FILLED}
							/>
						</div>
					) : null}
					<CommentReactions
						commentId={comment.id}
						reactions={comment.reactions || []}
						viewer={{
							id: viewer.actualPersonId,
							fullName: [viewer.firstName, viewer.lastName].filter(Boolean).join(' '),
						}}
						readOnly={isTaskReadOnly}
					/>
				</div>
				{(isViewerComment || hasPermission(PERMISSION_TYPE.COMMENT_DELETE_ALL)) && !isTaskReadOnly ? (
					<ActionsMenu
						className="time-entry-actions"
						disabled={isTaskReadOnly}
						options={actionMenuOptions}
						whiteInner={true}
					/>
				) : null}
			</div>
		);
	}
}

Comment.propTypes = {
	comment: PropTypes.object.isRequired,
};

export default injectIntl(Comment);
